import { Component, ErrorInfo, ReactNode } from 'react';
import { isV2LayoutPage, reportError } from '@v2/shared/lib/errors/reportError';

type Props = {
  /** The mounted component's name, sent with the report. */
  name: string;
  children: ReactNode;
  /**
   * What to draw instead of a crashed subtree, when it is not the page's own message. An
   * optional piece of a page (a wallet button) passes `null`, so a crash takes out only that
   * piece and leaves the page around it standing.
   */
  fallback?: ReactNode;
  /** Called once the crash has been caught, for a parent that was waiting on the crashed child. */
  onError?: () => void;
};

type State = { failed: boolean; silent: boolean };

/**
 * On a legacy page the mount renders nothing after a crash, which is what React did there
 * before this boundary existed; the message is for V2 pages, where the layout's header and
 * footer are still standing around it.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, silent: false };

  static getDerivedStateFromError(): State {
    return { failed: true, silent: !isV2LayoutPage() };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, { source: 'render', component: this.props.name, componentStack: info.componentStack });

    try {
      this.props.onError?.();
    } catch {
      // The report has gone; a parent's callback failing must not raise a second crash from here.
    }
  }

  render() {
    if (!this.state.failed) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    if (this.state.silent) {
      return null;
    }

    return (
      <div role="alert" className="mx-auto max-w-2xl px-4 py-12 text-center">
        Something went wrong. Please refresh the page.
      </div>
    );
  }
}
